// @givmo/app-admin — one honest clipboard write for the company sign-in surface (AS-WEB7).
//
// ⚠ WHY THIS IS NOT `await navigator.clipboard?.writeText(value)`. That expression looks like it
// handles a missing clipboard and does the opposite: the optional call short-circuits to
// `undefined`, `await undefined` RESOLVES, and control lands in the SUCCESS branch. The screen then
// says "copied" having copied nothing — and it does so exactly where the API is absent, because
// `navigator.clipboard` is undefined in an INSECURE CONTEXT (plain `http`, which is how an admin
// reaches a local or an internally-hosted build). A `catch` cannot see it; there is nothing to
// catch.
//
// That matters most on the one screen this repo has where the value cannot be re-fetched: a SCIM
// bearer token exists only in the response that minted it, so an Owner told "copied" who then
// dismisses the panel has lost a credential and must rotate. The same expression shipped on the DNS
// hand-off block beside it (AS-WEB6), where the cost is lower but the claim is just as false; both
// call this now.
//
// Returns a verdict rather than throwing, because every caller's remedy is identical and local: say
// it did not copy, and leave the value on screen to be selected by hand.

/** Whether the value actually reached the clipboard. `false` covers refusal AND absence alike. */
export async function copyToClipboard(value: string): Promise<boolean> {
  const clipboard: Clipboard | undefined = navigator.clipboard;
  if (clipboard === undefined || typeof clipboard.writeText !== 'function') return false;
  try {
    await clipboard.writeText(value);
    return true;
  } catch {
    // A permissions-policy denial, a non-focused document, or a user refusal. All the same remedy.
    return false;
  }
}
